import React from 'react'
import { withRouter } from 'react-router'

import { PageType } from 'src/apollo/sites'
import { RequestContextProps } from 'src/lib/js/context'
import { DebugContextProvider } from 'src/shared/components/common/debug/DebugContext'

import AttributionContextProvider from 'shared/components/common/attribution_context/AttributionContext'
import RestaurantContextProvider, {
  useRestaurant
} from 'shared/components/common/restaurant_context/RestaurantContext'
import { RestaurantRoutesContextProvider } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'
import { RxDataUsage } from 'shared/js/hooks/useDefaultSiteData'

import { GlobalNavContextProvider } from 'public/components/default_template/GlobalNavContext'
import { SpotlightContextProvider } from 'public/components/default_template/spotlight/SpotlightContext'
import { ReservationContextProvider } from 'public/components/online_ordering/ReservationContext'
import WrapWithCartRelatedProviders, {
  WRAP_WITH_CART_RELATED_PROVIDERS_DENY_LIST
} from 'public/components/pages/WrapWithCartRelatedProviders'

import LocationSelectionGate from 'public/components/default_template/online_ordering/location_selection/LocationSelectionGate'

import { PageShellInfrastructure } from './PageShellInfrastructure'
import urljoin from 'url-join'

type WrappedProps = RequestContextProps & {
  titleTag?: string
  descriptionTag?: string
  homePath?: string
  orderPath?: string
  ecommPath?: string
  accountPath?: string
  orderPathPattern?: string
  checkoutPathPrefix?: string
  confirmPathPrefix?: string
  noPopups?: boolean
}

type Props = WrappedProps & {
  ooUsage?: RxDataUsage
  sitesUsage?: RxDataUsage
  // used to identify the page type for the current page so that we can know whether to add cart-related providers and
  // enable showCartInNavigation functionality
  pageType?: PageType
}

const WrappedPageShell = (props: React.PropsWithChildren<WrappedProps>) => {
  const {
    selectedLocation: { shortUrl },
    ecommSettings,
    isEcommMultiLocationEnabled
  } = useRestaurant()

  const ecommPath =
    !!ecommSettings?.enabledRestaurantGuids?.length &&
    !!shortUrl &&
    isEcommMultiLocationEnabled
      ? urljoin(props.ecommPath ?? '', shortUrl)
      : props.ecommPath

  return (
    <RestaurantRoutesContextProvider
      shortUrl={shortUrl}
      homePath={props.homePath}
      orderPath={props.orderPath}
      baseEcommPath={props.ecommPath}
      ecommPath={ecommPath}
      orderPathPattern={props.orderPathPattern}
      checkoutPathPrefix={props.checkoutPathPrefix}
      confirmPathPrefix={props.confirmPathPrefix}
      accountPath={props.accountPath}
    >
      <PageShellInfrastructure
        titleTag={props.titleTag}
        descriptionTag={props.descriptionTag}
        staticContext={props.staticContext}
        noPopups={props.noPopups}
      >
        {props.children}
      </PageShellInfrastructure>
    </RestaurantRoutesContextProvider>
  )
}

const ContextualPageShell = ({
  ooUsage,
  sitesUsage,
  children,
  pageType,
  ...rest
}: React.PropsWithChildren<Props>) => {
  const shouldWrapWithCartRelatedProviders =
    pageType && !WRAP_WITH_CART_RELATED_PROVIDERS_DENY_LIST.includes(pageType)
  return (
    <SpotlightContextProvider>
      <AttributionContextProvider>
        <DebugContextProvider>
          <RestaurantContextProvider
            ooUsage={ooUsage}
            sitesUsage={sitesUsage}
            isEditor={false}
            currentPageType={pageType}
            ecommPath={rest.ecommPath}
          >
            <ReservationContextProvider>
              <GlobalNavContextProvider>
                <WrappedPageShell {...rest}>
                  {shouldWrapWithCartRelatedProviders ? (
                    <WrapWithCartRelatedProviders pageType={pageType}>
                      {/* LocationSelectionGate is also mounted in EcommerceProvidersWrapper.
                          These two placements are mutually exclusive: PageShell handles
                          standard OO pages while EcommerceProvidersWrapper handles ecommerce routes. */}
                      {ooUsage === RxDataUsage.Required ? (
                        <LocationSelectionGate>
                          {children}
                        </LocationSelectionGate>
                      ) : (
                        children
                      )}
                    </WrapWithCartRelatedProviders>
                  ) : ooUsage === RxDataUsage.Required ? (
                    <LocationSelectionGate>{children}</LocationSelectionGate>
                  ) : (
                    children
                  )}
                </WrappedPageShell>
              </GlobalNavContextProvider>
            </ReservationContextProvider>
          </RestaurantContextProvider>
        </DebugContextProvider>
      </AttributionContextProvider>
    </SpotlightContextProvider>
  )
}
export default withRouter<
  React.PropsWithChildren<Props>,
  React.ComponentType<Props>
>(ContextualPageShell)
